import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/management/cuisines/source/mappings';
export const getCrmSourceCuisineMappingsEndpointIdentifier = endpoint;

export interface ICrmSourceCuisineMappingsData {
  id: number;
  sourceName: string;
  sourcePlatform: string;
  mappingConfidence: number;
  isVerified: boolean;
  masterCuisine: {
    id: number;
    name: string;
  } | null;
}

type TData = {
  pageCount: number;
  total: number;
  data: ICrmSourceCuisineMappingsData[];
};

type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    sourcePlatform?: string;
    name?: string;
  }
];

interface AdditionalParams {
  sourcePlatform?: string;
  name?: string;
}

type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & Record<string, never>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmSourceCuisineMappingsApi(additionalParams?: AdditionalParams, options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort } = getPageParams();
  const { sourcePlatform, name } = additionalParams || {};
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
        sort,
      };

      if (sourcePlatform) {
        params.sourcePlatform = sourcePlatform;
      }

      if (name) {
        params.name = name;
      }

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sort,
        sourcePlatform,
        name,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...otherOptions,
    }
  );
}
